import { createEvent, type EventAttributes } from 'ics';

import type { DateString } from '@/types/api';
import type { Appointment } from '@/types/scheduling';
import type { Student } from '@/types/student';
import { toCenterTime } from '@/utils/timezone';
import { addMinutes, format } from 'date-fns';

const generateRecurrenceRule = (appt: Appointment): string | undefined => {
  if (!appt.recurring_schedule) return undefined;

  const intervalMap = {
    weekly: 1,
    biweekly: 2,
    fourWeekly: 4,
  };

  const interval = intervalMap[appt.recurring_schedule.recurrence_pattern];
  let rrule = `FREQ=WEEKLY;INTERVAL=${interval}`;

  if (appt.recurring_schedule.effective_until) {
    const recurrenceEndDate = new Date(appt.recurring_schedule.effective_until);
    recurrenceEndDate.setUTCHours(23, 59, 59, 999);
    const until = format(recurrenceEndDate, "yyyyMMdd'T'HHmmss'Z'");
    rrule += `;UNTIL=${until}`;
  }

  return rrule;
};

interface GenerateCalendarFileOptions {
  appointment: Appointment;
  students: Student[];
  title: string;
  description: string;
  location: string;
  /** The clock the event's wall-clock times are written on, resolved from the `calendarExport` policy. */
  timeZone: string;
  organizer?: { name?: string; email?: string };
  notBookedAppointments?: { date: DateString }[];
}

/**
 * `ics` writes a bare date array as a floating local time, so these numbers are what the guardian's
 * calendar app will show. Which clock they are read on comes from the `calendarExport` rule in
 * TIME_DISPLAY_POLICY — callers resolve it via `useTimeFor` and pass it in, so flipping that rule
 * actually changes the exported event.
 */
export const generateCalendarFile = ({
  appointment,
  students,
  title,
  description,
  location,
  timeZone,
  organizer,
  notBookedAppointments = [],
}: GenerateCalendarFileOptions) => {
  const appointmentDate = toCenterTime(appointment.appointment_date, timeZone);

  const startDate: [number, number, number, number, number] = [
    appointmentDate.getFullYear(),
    appointmentDate.getMonth() + 1,
    appointmentDate.getDate(),
    appointmentDate.getHours(),
    appointmentDate.getMinutes(),
  ];

  const endDate = addMinutes(appointmentDate, appointment.duration_minutes);
  const endDateArray: [number, number, number, number, number] = [
    endDate.getFullYear(),
    endDate.getMonth() + 1,
    endDate.getDate(),
    endDate.getHours(),
    endDate.getMinutes(),
  ];

  const generateExclusionDates = (dates: { date: string }[]) => {
    const originalHour = appointmentDate.getHours();
    const originalMinute = appointmentDate.getMinutes();

    return dates.map((d) => {
      const exDate = toCenterTime(d.date, timeZone);
      return [exDate.getFullYear(), exDate.getMonth() + 1, exDate.getDate(), originalHour, originalMinute] as [number, number, number, number, number];
    });
  };

  const recurrenceRule = generateRecurrenceRule(appointment);
  const exclusionDates = generateExclusionDates(notBookedAppointments);

  const event: EventAttributes = {
    start: startDate,
    end: endDateArray,
    title,
    description,
    location,
    status: 'CONFIRMED',
    busyStatus: 'BUSY',
    organizer: {
      name: organizer?.name ?? 'Mathnasium',
      email: organizer?.email,
    },
    alarms: [
      {
        action: 'display',
        description,
        trigger: {
          hours: 1,
          minutes: 0,
          before: true,
        },
      },
    ],
    ...(recurrenceRule && { recurrenceRule }),
    ...(exclusionDates.length > 0 && { exclusionDates }),
  };

  createEvent(event, (error, value) => {
    if (error) {
      console.error('Error creating calendar event:', error);
      return;
    }

    const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);

    const student = students.find((s) => s.id === appointment.student_id);
    // eslint-disable-next-line unicorn/prefer-string-replace-all
    const sanitizedFullName = `${student?.firstName} ${student?.lastName}`.replace(/[^a-zA-Z0-9-]/g, '_');

    link.download = `mathnasium-appointment-${sanitizedFullName}-${format(appointmentDate, 'yyyy-MM-dd')}.ics`;
    document.body.append(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(link.href);
  });
};
